/**
 * Product Hero Gallery carousel behavior.
 *
 * Owns hero image state, focal positioning, neighbor preloading, pagination,
 * arrow navigation, and touch swipe without initializing the public runtime.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryCarousel';

  if (window[MODULE_KEY]?.install) return;

  class ProductHeroGalleryCarouselBehavior {
    initializeHeroCarouselState() {
      this.heroSwipeClickTimer = null;
      this.currentIndex = 0;
      this.images = this.heroBanner.querySelectorAll('.product__hero-image');
      /** Latest requested index; stale preload completions must not update the hero. */
      this._pendingHeroIndex = null;
    }

    destroyHeroCarousel() {
      this._pendingHeroIndex = null;
      if (this.heroSwipeClickTimer) {
        window.clearTimeout(this.heroSwipeClickTimer);
        this.heroSwipeClickTimer = null;
      }
    }

    syncCurrentIndexFromDom() {
      this.images = this.heroBanner.querySelectorAll('.product__hero-image');
      if (!this.images.length) {
        this.currentIndex = 0;
        return;
      }

      const visibleIndex = Array.from(this.images).findIndex(
        (slide) => !slide.classList.contains('hidden'),
      );
      if (visibleIndex >= 0) {
        this.currentIndex = visibleIndex;
      } else {
        this.currentIndex = Math.min(this.currentIndex, this.images.length - 1);
      }
    }

    ensureVisibleHeroState() {
      if (!this.images.length) return;

      let hasVisible = false;
      this.images.forEach((slide, index) => {
        const isVisible = index === this.currentIndex;
        slide.classList.toggle('hidden', !isVisible);
        if (isVisible) hasVisible = true;
      });

      if (!hasVisible) {
        this.images[0].classList.remove('hidden');
        this.currentIndex = 0;
      }

      this.updatePagination();
    }

    /**
     * Portrait images in a wide hero need center crop; landscape keeps its
     * existing anchor.
     */
    setHeroBackgroundFocal(slide) {
      if (!slide) return;
      const img = slide.querySelector('img.product__hero-image-img');
      if (!img) return;
      const apply = () => {
        if (this.isDestroyed) return;
        if (!img.naturalWidth || !img.naturalHeight) return;
        const portrait = img.naturalHeight > img.naturalWidth;
        slide.style.backgroundPosition = portrait ? 'center center' : '';
        img.style.objectPosition = portrait ? 'center center' : '';
      };
      if (img.complete && img.naturalWidth > 0) {
        apply();
      } else {
        img.addEventListener('load', apply, {
          once: true,
          signal: this.eventAbortController.signal,
        });
      }
    }

    prepareHeroImage(slide) {
      if (!slide) return null;
      const existing = slide.querySelector('img.product__hero-image-img');
      if (existing) return existing;
      const template = slide.querySelector('template[data-hero-image-source]');
      const source = template?.content?.querySelector('img.product__hero-image-img');
      if (!source) return null;
      const image = source.cloneNode(true);
      image.loading = 'eager';
      template.replaceWith(image);
      return image;
    }

    getHeroSlideImageUrl(slide) {
      if (!slide) return '';
      const inner = slide.querySelector('img.product__hero-image-img');
      if (inner && (inner.currentSrc || inner.src)) {
        return inner.currentSrc || inner.src;
      }
      const style = slide.getAttribute('style') || '';
      const match = style.match(/url\(\s*['"]?([^'")]+)['"]?\s*\)/i);
      return match ? match[1].trim() : '';
    }

    preloadImageUrl(url, sourceImage = null) {
      if (!url) return Promise.resolve();
      if (sourceImage) {
        if (sourceImage.complete && sourceImage.naturalWidth > 0) {
          return Promise.resolve(true);
        }
        return new Promise((resolve) => {
          const finish = (loaded) => {
            sourceImage.removeEventListener('load', onLoad);
            sourceImage.removeEventListener('error', onError);
            resolve(loaded);
          };
          const onLoad = () => finish(sourceImage.naturalWidth > 0);
          const onError = () => finish(false);
          sourceImage.addEventListener('load', onLoad, {
            once: true,
            signal: this.eventAbortController.signal,
          });
          sourceImage.addEventListener('error', onError, {
            once: true,
            signal: this.eventAbortController.signal,
          });
          sourceImage.loading = 'eager';
          if (sourceImage.complete) finish(sourceImage.naturalWidth > 0);
        });
      }
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = resolve;
        image.src = url;
      });
    }

    preloadHeroNeighbors(centerIndex) {
      const imageCount = this.images.length;
      if (imageCount <= 1) return;
      const indexes = new Set([
        centerIndex,
        (centerIndex - 1 + imageCount) % imageCount,
        (centerIndex + 1) % imageCount,
      ]);
      indexes.forEach((index) => {
        const slide = this.images[index];
        const sourceImage = this.prepareHeroImage(slide);
        if (sourceImage) {
          sourceImage.loading = 'eager';
          return;
        }
        const url = this.getHeroSlideImageUrl(slide);
        if (!url) return;
        const image = new Image();
        image.src = url;
      });
    }

    /**
     * Switches the visible image after its source is ready, preventing a white
     * flash and ignoring stale preload completions after rapid navigation.
     */
    showImage(index) {
      if (index < 0) index = this.images.length - 1;
      if (index >= this.images.length) index = 0;
      if (this.images.length === 0 || index === this.currentIndex) return;

      const nextSlide = this.images[index];
      const innerImg = this.prepareHeroImage(nextSlide);
      const url = this.getHeroSlideImageUrl(nextSlide);

      const apply = () => {
        if (this.isDestroyed || this._pendingHeroIndex !== index) return;
        this.currentIndex = index;
        this.images.forEach((image) => image.classList.add('hidden'));
        this.images[this.currentIndex]?.classList.remove('hidden');
        this.setHeroBackgroundFocal(this.images[this.currentIndex]);
        this.updatePagination();
        this.preloadHeroNeighbors(index);
      };

      this._pendingHeroIndex = index;

      if (innerImg && innerImg.complete && innerImg.naturalWidth > 0) {
        apply();
        return;
      }

      this.preloadImageUrl(url, innerImg).then((loaded) => {
        if (loaded !== false) apply();
      });
    }

    updatePagination() {
      const dots = this.heroBanner.querySelectorAll('[data-hero-dot]');
      if (!dots.length) return;
      dots.forEach((dot, index) => {
        const active = index === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    initNavigation() {
      const prevBtn = this.heroBanner.querySelector('[data-hero-nav="prev"]');
      const nextBtn = this.heroBanner.querySelector('[data-hero-nav="next"]');
      const dots = this.heroBanner.querySelectorAll('[data-hero-dot]');

      if (this.images.length <= 1) return;

      prevBtn?.addEventListener('click', () => this.showImage(this.currentIndex - 1), {
        signal: this.eventAbortController.signal,
      });
      nextBtn?.addEventListener('click', () => this.showImage(this.currentIndex + 1), {
        signal: this.eventAbortController.signal,
      });
      dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index = parseInt(dot.getAttribute('data-hero-dot') || '0', 10);
            this.showImage(index);
          },
          { signal: this.eventAbortController.signal },
        );
      });
    }

    initTouchNavigation() {
      if (!this.heroMedia || this.images.length <= 1) return;

      const interactiveSelector =
        'button, a, input, textarea, select, summary, [role="button"], [data-hero-nav], [data-hero-dot], [data-media-open], [data-mobile-media-hub-open]';
      const swipeThreshold = 36;
      const axisLockThreshold = 10;
      let trackingTouch = false;
      let activePointerId = null;
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let deltaY = 0;
      let axisLocked = null;
      let suppressNextClick = false;

      const resetTouchState = () => {
        trackingTouch = false;
        activePointerId = null;
        startX = 0;
        startY = 0;
        deltaX = 0;
        deltaY = 0;
        axisLocked = null;
      };

      const beginTracking = (target, clientX, clientY, pointerId = null) => {
        if (target?.closest(interactiveSelector)) {
          resetTouchState();
          return false;
        }

        trackingTouch = true;
        activePointerId = pointerId;
        startX = clientX;
        startY = clientY;
        deltaX = 0;
        deltaY = 0;
        axisLocked = null;
        return true;
      };

      const trackMove = (clientX, clientY) => {
        if (!trackingTouch) return false;

        deltaX = clientX - startX;
        deltaY = clientY - startY;

        if (!axisLocked) {
          const absX = Math.abs(deltaX);
          const absY = Math.abs(deltaY);

          if (absX < axisLockThreshold && absY < axisLockThreshold) return false;
          axisLocked = absX > absY ? 'x' : 'y';
        }

        return axisLocked === 'x';
      };

      const finishTracking = () => {
        if (!trackingTouch) {
          resetTouchState();
          return;
        }

        if (
          axisLocked === 'x' &&
          Math.abs(deltaX) >= swipeThreshold &&
          Math.abs(deltaX) > Math.abs(deltaY)
        ) {
          suppressNextClick = true;
          this.showImage(
            deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1,
          );

          window.clearTimeout(this.heroSwipeClickTimer);
          this.heroSwipeClickTimer = window.setTimeout(() => {
            suppressNextClick = false;
            this.heroSwipeClickTimer = null;
          }, 250);
        }

        resetTouchState();
      };

      this.heroMedia.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType !== 'touch') return;
          beginTracking(event.target, event.clientX, event.clientY, event.pointerId);
        },
        { passive: true, signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'touch' || event.pointerId !== activePointerId) return;
          if (trackMove(event.clientX, event.clientY)) event.preventDefault();
        },
        { passive: false, signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'pointerup',
        (event) => {
          if (event.pointerType !== 'touch' || event.pointerId !== activePointerId) return;
          finishTracking();
        },
        { signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'pointercancel',
        (event) => {
          if (event.pointerType !== 'touch' || event.pointerId !== activePointerId) return;
          resetTouchState();
        },
        { signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'touchstart',
        (event) => {
          if (window.PointerEvent) return;
          const touch = event.touches?.[0];
          if (!touch) return;
          beginTracking(event.target, touch.clientX, touch.clientY);
        },
        { passive: true, signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'touchmove',
        (event) => {
          if (window.PointerEvent || !trackingTouch) return;
          const touch = event.touches?.[0];
          if (!touch) return;
          if (trackMove(touch.clientX, touch.clientY)) event.preventDefault();
        },
        { passive: false, signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'touchend',
        () => {
          if (!window.PointerEvent) finishTracking();
        },
        { signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'touchcancel',
        () => {
          if (!window.PointerEvent) resetTouchState();
        },
        { signal: this.eventAbortController.signal },
      );

      this.heroMedia.addEventListener(
        'click',
        (event) => {
          if (!suppressNextClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, signal: this.eventAbortController.signal },
      );
    }
  }

  const descriptors = Object.getOwnPropertyDescriptors(
    ProductHeroGalleryCarouselBehavior.prototype,
  );
  delete descriptors.constructor;

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError('Product Hero Gallery carousel requires a class constructor.');
    }

    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
